<template>
  <section class="profile-detail-wrap">
    <div class="pd-hero" :class="{ 'has-background': hasProfileBackgroundImage }" :style="heroStyle">
      <div class="pd-nav">
        <div class="pd-title">{{ profileInfo?.nickname || '-' }}</div>
      </div>
    </div>

    <div class="pd-profile-card">
      <div class="pd-summary">
        <div class="summary-head">
          <div class="avatar" :style="{ backgroundImage: avatarStyle }" />
        </div>
        <div class="meta">
          <div class="name-row">
            <span class="name">{{ profileInfo?.nickname || '-' }}</span>
            <span class="identity" v-if="isMerchant">商家</span>
          </div>
          <div class="city" v-if="cityAndIdentity">{{ cityAndIdentity }}</div>
          <div class="signature" v-if="profileInfo?.signature">{{ profileInfo?.signature }}</div>
        </div>
      </div>
      <div class="stats">
        <div class="stat-item" v-for="item in stats" :key="item.label">
          <span class="value">{{ item.value }}</span>
          <span class="label">{{ item.label }}</span>
        </div>
      </div>
    </div>

    <div class="pd-section" v-if="pets.length > 0">
      <div class="section-title">养宠{{ petDays }}天</div>
      <div class="pet-list">
        <div class="pet-card" v-for="pet in pets" :key="pet.id || pet.petId || pet.nickname">
          <div class="pet-avatar" :style="{ backgroundImage: `url(${petCover(pet)})` }" />
          <div class="pet-meta">
            <div class="pet-name">{{ pet.nickname || '-' }}</div>
            <div class="pet-breed">{{ petBreedText(pet) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="pd-section" v-if="showMerchantSection">
      <div class="section-title">商家信息</div>
      <div class="merchant-card">
        <div class="merchant-tags" v-if="merchantTags.length > 0">
          <span v-for="tag in merchantTags" :key="tag.name">{{ tag.name }}</span>
        </div>
        <div class="merchant-desc" v-if="merchantInfo?.merchantIntroText">{{ merchantInfo?.merchantIntroText }}</div>
        <div class="merchant-address" v-if="merchantAddress">{{ merchantAddress }}</div>
        <div class="merchant-hours" v-if="merchantInfo?.businessHours">营业时间：{{ merchantInfo?.businessHours }}</div>
        <div class="merchant-images" v-if="merchantImages.length > 0">
          <div
            class="merchant-image"
            v-for="(image, index) in merchantImages"
            :key="`${image}-${index}`"
            :style="{ backgroundImage: `url(${image})` }"
          />
        </div>
      </div>
    </div>

    <div class="pd-section" v-if="posts.length > 0">
      <div class="section-title">最新动态</div>
      <div class="post-list">
        <a
          class="post-card"
          v-for="post in posts"
          :key="post.postId"
          :href="`/post/detail?id=${post.postId}`"
        >
          <div class="post-head">
            <div class="post-author-avatar" :style="{ backgroundImage: `url(${postAuthorAvatar(post)})` }" />
            <div class="post-author-meta">
              <div class="post-author-name">{{ post.author?.nickname || profileInfo?.nickname || '-' }}</div>
              <div class="post-time">{{ formatPostTime(post.publishedTime) }}</div>
            </div>
          </div>
          <div class="post-title" v-if="post.title">{{ post.title }}</div>
          <div class="post-content" v-if="post.content">{{ post.content }}</div>
          <div class="post-media" v-if="postPreviewImages(post).length > 0">
            <div
              class="post-media-item"
              v-for="(image, index) in postPreviewImages(post)"
              :key="`${post.postId}-${image}-${index}`"
              :style="{ backgroundImage: `url(${image})` }"
            >
              <div class="post-video-play" v-if="isVideoPost(post) && index === 0">
                <span class="post-video-play-icon" />
              </div>
            </div>
          </div>
          <div class="post-meta">
            <span>评论 {{ post.commentCount ?? 0 }}</span>
            <span>获赞 {{ post.likeCount ?? 0 }}</span>
          </div>
        </a>
      </div>
    </div>

    <div class="downloadbtn" @click="openChongyiDownload"><span>打开 宠一APP</span></div>
  </section>
</template>

<script setup>
  import { computed, onMounted, ref } from 'vue';
  import moment from 'moment';
  import * as Request from '@/assets/request.js';
  import { getSearchByKey, openChongyiDownload, setStaticUrl } from '@/assets/utils.js';

  const id = getSearchByKey('id')
  const data = ref(null)
  const posts = ref([])

  const profileInfo = computed(() => data.value?.profileInfo || null)
  const statsInfo = computed(() => data.value?.statsInfo || null)
  const merchantInfo = computed(() => data.value?.merchantInfo || null)
  const pets = computed(() => profileInfo.value?.petList || [])
  const petDays = computed(() => profileInfo.value?.petDays || 0)
  const isMerchant = computed(() => profileInfo.value?.userIdentity === '2')
  const profileBackgroundImage = computed(() => setStaticUrl(profileInfo.value?.profileBackgroundImage || ''))
  const hasProfileBackgroundImage = computed(() => Boolean(profileBackgroundImage.value))

  const avatarStyle = computed(() => {
    const avatar = setStaticUrl(profileInfo.value?.avatar || '')
    return avatar ? `url(${avatar})` : ''
  })

  const heroStyle = computed(() => {
    if (!hasProfileBackgroundImage.value) {
      return {}
    }
    return {
      backgroundImage: `linear-gradient(180deg, rgba(28, 31, 36, 0.22) 0%, rgba(28, 31, 36, 0.48) 100%), url(${profileBackgroundImage.value})`
    }
  })

  const cityAndIdentity = computed(() => {
    const city = `${profileInfo.value?.residentCityName || ''}`.trim()
    const identity = (profileInfo.value?.dogOwnerTypes || [])
      .map(item => `${item || ''}`.trim())
      .find(Boolean) || ''
    return [city, identity].filter(Boolean).join(' / ')
  })

  const stats = computed(() => {
    const current = statsInfo.value || {}
    return [
      { label: '关注', value: current.followCount ?? 0 },
      { label: '粉丝', value: current.fansCount ?? 0 },
      { label: '动态', value: current.postCount ?? 0 },
      { label: '获赞', value: current.likeCount ?? 0 },
      { label: '帮助', value: current.helpCount ?? 0 },
    ]
  })

  const merchantTags = computed(() => {
    const list = merchantInfo.value?.merchantTypeList || []
    return list.filter(item => `${item?.name || ''}`.trim())
  })

  const merchantImages = computed(() => {
    const list = merchantInfo.value?.merchantIntroImages || []
    return list.map(item => setStaticUrl(item)).filter(Boolean)
  })

  const merchantAddress = computed(() => {
    const merchant = merchantInfo.value
    if (!merchant) return ''
    const region = [
      merchant.merchantProvinceName || '',
      merchant.merchantCityName || '',
      merchant.merchantAreaName || '',
    ].join('')
    return `${region}${merchant.merchantAddress || ''}`.trim()
  })

  const showMerchantSection = computed(() => {
    if (!isMerchant.value || !merchantInfo.value) return false
    return Boolean(
      merchantTags.value.length > 0 ||
      merchantImages.value.length > 0 ||
      merchantInfo.value?.merchantIntroText ||
      merchantAddress.value ||
      merchantInfo.value?.businessHours
    )
  })

  const petCover = (pet) => {
    return setStaticUrl(pet?.avatar || pet?.images?.[0] || '')
  }

  const petBreedText = (pet) => {
    const parts = [`${pet?.breed || ''}`.trim()]
    return parts.filter(Boolean).join(' / ') || '-'
  }

  const isVideoPost = (post) => `${post?.postType || 'image'}` === 'video'

  const postMediaItems = (post) => {
    const mediaInfo = Array.isArray(post?.mediaInfo) ? post.mediaInfo : []
    if (mediaInfo.length > 0) {
      return mediaInfo.filter(item => `${item?.url || ''}`.trim())
    }
    const images = Array.isArray(post?.images) ? post.images : []
    return images
      .map(item => `${item || ''}`.trim())
      .filter(Boolean)
      .map(url => ({ url, type: 'image' }))
  }

  const postPreviewImages = (post) => {
    const medias = postMediaItems(post)
    if (isVideoPost(post)) {
      const firstVideo = medias.find(item => `${item?.type || 'image'}` === 'video')
      return firstVideo?.url ? [setStaticUrl(firstVideo.url)] : []
    }
    return medias
      .filter(item => `${item?.type || 'image'}` !== 'video')
      .slice(0, 3)
      .map(item => setStaticUrl(item.url))
  }

  const postAuthorAvatar = (post) => {
    return setStaticUrl(post?.author?.avatar || '')
  }

  const formatPostTime = (time) => {
    return time ? moment(time).format('MM.DD HH:mm') : '-'
  }

  const getData = async () => {
    if (!id) return
    const result = await Request.post({
      url: '/member/user/profile',
      data: {
        userId: id,
      }
    }, true)
    if (!result || result.error) return
    data.value = result.data
  }

  const getPosts = async () => {
    if (!id) return
    const result = await Request.post({
      url: '/post/userPostList',
      data: {
        userId: id,
        pageSize: 3,
      }
    }, true)
    if (!result || result.error) return
    posts.value = result.data?.list || []
  }

  onMounted(() => {
    getData()
    getPosts()
  })
</script>

<style scoped lang="less">
@import "index.less";
</style>
